import React, { useEffect, useMemo, useState } from 'react';
import {
    Text,
    Center,
    Loader,
    Checkbox,
    Stack,
    Flex,
    TextInput,
    Button,
    Title,
    Group,
} from '@mantine/core';
import ShiftSignupCard from './ShiftSignupCard';
import { Shift } from '../types/Shift';
import { IconSearch, IconSortAscending, IconSortDescending } from '@tabler/icons-react';
import { getAuthHeaders } from "../api/authHelper";

interface ShiftListProps {
    groupId: number;
    eventId: number;
    language: string;
    onRegistrationChanged?: () => void;
}

const ShiftList: React.FC<ShiftListProps> = ({ groupId, language, onRegistrationChanged, }) => {
    const [shifts, setShifts] = useState<Shift[]>([]);
    const [loading, setLoading] = useState(true);
    const [showOnlyAvailable, setShowOnlyAvailable] = useState(false);
    const [showPastShifts, setShowPastShifts] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [sortAsc, setSortAsc] = useState(true);

    const personId = Number(sessionStorage.getItem("personId")) || 0;


    const [registeredShiftIds, setRegisteredShiftIds] = useState<number[]>([]);
    const registeredSet = useMemo(() => new Set(registeredShiftIds), [registeredShiftIds]);

    useEffect(() => {
        const fetchAll = async () => {
            try {
                const apiUrl = process.env.REACT_APP_API_URL;

                // 🔓 PUBLIC endpoint (ingen auth)
                const shiftsRes = await fetch(`${apiUrl}/api/events/${groupId}/available`);
                const shiftsData = await shiftsRes.json();
                setShifts(shiftsData);

                // 🔒 PROTECTED endpoint (MED auth)
                const regRes = await fetch(
                    `${apiUrl}/api/shifts/registrations?personId=${personId}`,
                    {
                        headers: getAuthHeaders(),
                    }
                );

                const regIds = await regRes.json();
                setRegisteredShiftIds(regIds);

            } catch (error) {
                console.error('Failed to fetch shifts/registrations:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchAll();
    }, [groupId, personId]);

    const handleRegistrationChanged = (shiftsId: number, isNowRegistered: boolean) => {
        setShifts((prev) =>
            prev.map((s) => {
                if (s.shiftsID !== shiftsId) return s;

                const nextSpots = isNowRegistered
                    ? Math.max(0, s.availableSpots - 1)
                    : s.availableSpots + 1;

                return { ...s, availableSpots: nextSpots };
            })
        );
    };

    const handleRegistrationChangedAndRefresh = (shiftsId: number, isNowRegistered: boolean) => {
        handleRegistrationChanged(shiftsId, isNowRegistered);

        setRegisteredShiftIds((prev) => {
            const set = new Set(prev);
            if (isNowRegistered) set.add(shiftsId);
            else set.delete(shiftsId);
            return Array.from(set);
        });

        onRegistrationChanged?.();
    };

    const todayStart = useMemo(() => {
        const d = new Date();
        d.setHours(0, 0, 0, 0);
        return d;
    }, []);

    const pastShiftCount = useMemo(() => {
        return shifts.filter((s) => new Date(s.startTime) < todayStart).length;
    }, [shifts, todayStart]);

    const filteredShifts = shifts
        .filter((s) => {
            const startDate = new Date(s.startTime);

            if (!showPastShifts && startDate < todayStart) {
                return false;
            }

            const term = searchTerm.trim().toLowerCase();

            const nameStr = (s.shiftsName ?? "").toLowerCase();
            const functionStr = (s.functionName ?? "").toLowerCase();

            const dayStr = startDate
                .toLocaleDateString("da-DK", { weekday: "long" })
                .toLowerCase();

            const dateStr = startDate.toLocaleDateString("da-DK");

            const dateStrPadded = startDate.toLocaleDateString("da-DK", {
                day: "2-digit",
                month: "2-digit",
                year: "numeric",
            });

            const dayOnly = startDate.toLocaleDateString("da-DK", {
                day: "2-digit",
            });

            const monthOnly = startDate.toLocaleDateString("da-DK", {
                month: "2-digit",
            });

            const normalizedTerm = term.replace(/\s/g, "").replace(/-/g, ".").replace(/\//g, ".");
            const normalizedDate = dateStr.replace(/\s/g, "");
            const normalizedDatePadded = dateStrPadded.replace(/\s/g, "");

            const matchesSearch =
                term === "" ||
                nameStr.includes(term) ||
                functionStr.includes(term) ||
                dayStr.includes(term) ||
                normalizedDate.includes(normalizedTerm) ||
                normalizedDatePadded.includes(normalizedTerm) ||
                dayOnly.includes(normalizedTerm) ||
                monthOnly.includes(normalizedTerm);

            const matchesAvailability = !showOnlyAvailable || (s.availableSpots ?? 0) > 0;

            return matchesAvailability && matchesSearch;
        })
        .sort((a, b) => {
            const dateA = new Date(a.startTime).getTime();
            const dateB = new Date(b.startTime).getTime();
            return sortAsc ? dateA - dateB : dateB - dateA;
        });

    if (loading) {
        return (
            <Center mt="md">
                <Loader color="red" />
            </Center>
        );
    }

    return (
        <div style={{ width: "min(900px, 100%)", margin: "0 auto", padding: "0 12px" }}>
            <Stack>
                <Flex gap="sm" justify="center" align="flex-end" wrap="wrap" mt="md">
                    <TextInput
                        style={{ flex: 1, minWidth: '240px', maxWidth: '400px' }}
                        label={language === 'da' ? 'Søg vagt eller dato' : 'Search shift or date'}
                        placeholder={language === 'da' ? 'f.eks. Fredag eller 17.04' : 'e.g. Friday or 17.04'}
                        value={searchTerm}
                        onChange={(event) => setSearchTerm(event.currentTarget.value)}
                        leftSection={<IconSearch size={16} />}
                    />

                    <Button
                        variant="light"
                        onClick={() => setSortAsc(!sortAsc)}
                        leftSection={sortAsc ? <IconSortAscending size={16} /> : <IconSortDescending size={16} />}
                        style={{ height: '40px' }}
                    >
                        {language === 'da' ? 'Sorter dato' : 'Sort date'}
                    </Button>
                </Flex>

                <Group justify="center" mt="sm">
                    <Checkbox
                        checked={showOnlyAvailable}
                        onChange={(event) => setShowOnlyAvailable(event.currentTarget.checked)}
                        label={language === 'da' ? 'Vis kun vagter med ledige pladser' : 'Only show available shifts'}
                    />

                    {pastShiftCount > 0 && (
                        <Button
                            size="xs"
                            variant={showPastShifts ? "filled" : "light"}
                            color="gray"
                            onClick={() => setShowPastShifts((prev) => !prev)}
                        >
                            {showPastShifts
                                ? language === 'da'
                                    ? 'Skjul tidligere vagter'
                                    : 'Hide past shifts'
                                : language === 'da'
                                    ? `Vis tidligere vagter (${pastShiftCount})`
                                    : `Show past shifts (${pastShiftCount})`}
                        </Button>
                    )}
                </Group>

                {filteredShifts.length === 0 ? (
                    <Text ta="center">
                        {language === 'da' ? 'Ingen vagter fundet.' : 'No shifts found.'}
                    </Text>
                ) : (
                    <>
                        <Title order={4}>
                            {showPastShifts
                                ? language === 'da'
                                    ? 'Alle vagter'
                                    : 'All shifts'
                                : language === 'da'
                                    ? 'Kommende vagter'
                                    : 'Upcoming shifts'}
                        </Title>

                        <Stack gap="sm">
                            {filteredShifts.map((shift) => (

                                <ShiftSignupCard
                                    key={shift.shiftsID}
                                    shiftsID={shift.shiftsID}
                                    shiftsName={shift.shiftsName}
                                    startTime={shift.startTime}
                                    endTime={shift.endTime}
                                    endsOneHourAfterPlay={shift.endsOneHourAfterPlay}
                                    availableSpots={shift.availableSpots}
                                    language={language}
                                    personId={personId}
                                    isInitiallyRegistered={registeredSet.has(shift.shiftsID)}
                                    allowWaitlist={shift.waitlistAllowed}
                                    onRegistrationChanged={(id, state) => {
                                        handleRegistrationChangedAndRefresh(id, state === "confirmed");
                                    }}
                                    currentUserId={Number(sessionStorage.getItem("userId") || 0)}
                                    allowDuplicateForUserIds={[]} // til flere tilmelding på en bruger
                                />
                                
                            ))}
                        </Stack>
                    </>
                )}
            </Stack>
        </div>
    );
};

export default ShiftList;